import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../../api';

// ---------------------------------------------------------------------------
// Avatar Section
// ---------------------------------------------------------------------------
const AvatarSection = ({ user, refreshAuth }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [avatars, setAvatars] = useState([]);
    const [isLoadingAvatars, setIsLoadingAvatars] = useState(false);
    const [avatarError, setAvatarError] = useState('');
    const [avatarSuccess, setAvatarSuccess] = useState('');
    const [isSetting, setIsSetting] = useState(false);

    const loadAvatars = async () => {
        setIsLoadingAvatars(true);
        setAvatarError('');
        try {
            const res = await fetch(`${API_URL}/avatars`);
            if (!res.ok) throw new Error('Failed to load avatars');
            const data = await res.json();
            setAvatars(data.avatars || []);
        } catch {
            setAvatarError('Unable to load avatars at this time.');
        } finally {
            setIsLoadingAvatars(false);
        }
    };

    const handleTogglePicker = () => {
        if (!showPicker && avatars.length === 0) {
            loadAvatars();
        }
        setShowPicker((prev) => !prev);
        setAvatarSuccess('');
        setAvatarError('');
    };

    const handleSelectAvatar = async (avatarUrl) => {
        setIsSetting(true);
        setAvatarError('');
        setAvatarSuccess('');
        try {
            const { tokens } = await fetchAuthSession();
            const token = tokens.idToken.toString();

            const res = await fetch(`${API_URL}/users/avatar`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ avatarUrl }),
            });

            if (!res.ok) throw new Error('Failed to update avatar');
            await refreshAuth();
            setAvatarSuccess('Avatar updated!');
            setShowPicker(false);
        } catch {
            setAvatarError('Unable to update avatar. Please try again.');
        } finally {
            setIsSetting(false);
        }
    };

    const initials = user?.displayName
        ? user.displayName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
        : '?';

    return (
        <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-xl font-semibold mb-4 text-white">Avatar</h3>

            {/* Current avatar display */}
            <div className="flex items-center gap-4 mb-4">
                {user?.avatarUrl ? (
                    <img
                        src={user.avatarUrl}
                        alt={user.displayName || 'Avatar'}
                        className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/40"
                    />
                ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-700 border-2 border-slate-600 flex items-center justify-center">
                        <span className="text-white font-semibold text-lg">{initials}</span>
                    </div>
                )}
                <button
                    onClick={handleTogglePicker}
                    className="text-sm bg-slate-700 hover:bg-slate-600 text-white px-4 py-1.5 rounded-full transition-colors"
                >
                    {showPicker ? 'Cancel' : 'Change Avatar'}
                </button>
            </div>

            {avatarSuccess && (
                <p className="text-green-400 text-sm mb-3">{avatarSuccess}</p>
            )}

            {avatarError && (
                <p className="text-red-400 text-sm mb-3">{avatarError}</p>
            )}

            {showPicker && (
                <div className="bg-slate-800/50 rounded-xl p-4">
                    {isLoadingAvatars && (
                        <div className="flex justify-center py-6">
                            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
                        </div>
                    )}
                    {!isLoadingAvatars && avatars.length === 0 && (
                        <p className="text-gray-500 text-sm italic text-center py-4">
                            No avatars available yet.
                        </p>
                    )}
                    {!isLoadingAvatars && avatars.length > 0 && (
                        <div className="grid grid-cols-5 sm:grid-cols-8 gap-3">
                            {avatars.map((avatar) => (
                                <button
                                    key={avatar.id || avatar.url}
                                    onClick={() => handleSelectAvatar(avatar.url)}
                                    disabled={isSetting}
                                    className={`rounded-full overflow-hidden border-2 transition-colors ${
                                        user?.avatarUrl === avatar.url
                                            ? 'border-amber-500'
                                            : 'border-transparent hover:border-amber-400/50'
                                    }`}
                                    title={avatar.name || 'Select avatar'}
                                    aria-label={`Select avatar: ${avatar.name || 'avatar'}`}
                                >
                                    <img
                                        src={avatar.url}
                                        alt={avatar.name || 'Avatar option'}
                                        className="w-10 h-10 object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Email Settings Section
// ---------------------------------------------------------------------------
const EmailSettingsSection = ({ userEmail }) => {
    const [subscribed, setSubscribed] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' | 'error'

    useEffect(() => {
        if (!userEmail) return;
        const loadPrefs = async () => {
            setIsLoading(true);
            try {
                const res = await fetch(`${API_URL}/subscribers/${encodeURIComponent(userEmail)}`);
                if (res.ok) {
                    const data = await res.json();
                    if (typeof data.subscribed === 'boolean') {
                        setSubscribed(data.subscribed);
                    }
                    setIsLoaded(true);
                } else {
                    // Email not in list yet — treat as unsubscribed
                    setSubscribed(false);
                    setIsLoaded(true);
                }
            } catch {
                setIsLoaded(true); // Allow interaction even on error
            } finally {
                setIsLoading(false);
            }
        };
        loadPrefs();
    }, [userEmail]);

    const handleSave = async (e) => {
        e.preventDefault();
        setMessage('');
        setIsSaving(true);
        try {
            const res = await fetch(`${API_URL}/subscribers/${encodeURIComponent(userEmail)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscribed }),
            });
            if (res.ok) {
                setMessage('Preferences saved.');
                setMessageType('success');
            } else {
                setMessage('Failed to save preferences.');
                setMessageType('error');
            }
        } catch {
            setMessage('Failed to save preferences.');
            setMessageType('error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-xl font-semibold mb-4 text-white">Email Preferences</h3>

            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <div className="px-4 py-2 bg-gray-700 rounded-lg text-gray-400 text-sm">
                    {userEmail}
                </div>
            </div>

            {isLoading && (
                <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <div className="w-4 h-4 border border-gray-500 border-t-transparent rounded-full animate-spin" />
                    Loading preferences...
                </div>
            )}

            {!isLoading && isLoaded && (
                <form onSubmit={handleSave} className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={subscribed}
                            onChange={(e) => setSubscribed(e.target.checked)}
                            className="w-4 h-4 accent-amber-500"
                        />
                        <span className="text-sm text-gray-300">Subscribe to Buddha Wisdom updates</span>
                    </label>

                    {message && (
                        <p className={`text-sm ${messageType === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                            {message}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={isSaving}
                        className="bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors"
                    >
                        {isSaving ? 'Saving...' : 'Save Preferences'}
                    </button>
                </form>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main Profile component
// ---------------------------------------------------------------------------
const Profile = () => {
    const { user, refreshAuth } = useAuth();
    const [displayName, setDisplayName] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (user?.displayName) {
            setDisplayName(user.displayName);
        }
    }, [user]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            await updateUserAttributes({
                userAttributes: {
                    name: displayName,
                },
            });

            await refreshAuth();

            setMessage('Profile updated successfully!');
            setIsEditing(false);
        } catch (err) {
            console.error('Error updating profile:', err);
            setError(err.message || 'Failed to update profile');
        } finally {
            setIsLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="flex justify-center items-center min-h-96">
                <div className="text-white text-xl">Please sign in to view your profile.</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white p-6">
            <div className="max-w-2xl mx-auto">
                <h1 className="text-3xl font-bold mb-8 text-center">Your Profile</h1>

                <div className="bg-gray-800 bg-opacity-90 rounded-lg p-8 space-y-6">
                    {/* Profile Information */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Email
                            </label>
                            <div className="px-4 py-2 bg-gray-700 rounded-lg text-gray-300">
                                {user.email}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Display Name
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                                    placeholder="Enter your display name"
                                />
                            ) : (
                                <div className="px-4 py-2 bg-gray-700 rounded-lg text-gray-300">
                                    {user.displayName || 'Not set'}
                                </div>
                            )}
                        </div>

                        {user.isAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Role
                                </label>
                                <div className="px-4 py-2 bg-yellow-900 bg-opacity-50 border border-yellow-600 rounded-lg text-yellow-200">
                                    Administrator
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Messages */}
                    {message && (
                        <div className="p-3 bg-green-600 bg-opacity-20 border border-green-500 rounded text-green-200 text-sm">
                            {message}
                        </div>
                    )}

                    {error && (
                        <div className="p-3 bg-red-600 bg-opacity-20 border border-red-500 rounded text-red-200 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={handleUpdateProfile}
                                    disabled={isLoading || !displayName.trim()}
                                    className="bg-amber-600 hover:bg-amber-500 disabled:bg-gray-600 px-6 py-2 rounded-lg font-semibold transition-colors"
                                >
                                    {isLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    onClick={() => {
                                        setIsEditing(false);
                                        setDisplayName(user.displayName || '');
                                        setError('');
                                        setMessage('');
                                    }}
                                    className="bg-gray-600 hover:bg-gray-700 px-6 py-2 rounded-lg font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-amber-600 hover:bg-amber-500 px-6 py-2 rounded-lg font-semibold transition-colors"
                            >
                                Edit Profile
                            </button>
                        )}
                    </div>

                    {/* Avatar Section */}
                    <AvatarSection user={user} refreshAuth={refreshAuth} />

                    {/* Email Settings Section */}
                    <EmailSettingsSection userEmail={user.email} />

                    {/* Virtual Temple Section */}
                    <div className="mt-8 pt-6 border-t border-gray-700">
                        <h3 className="text-xl font-semibold mb-4">Virtual Temple</h3>
                        <p className="text-gray-300 mb-4">
                            Access sacred practices and teachings in our immersive Virtual Temple experience.
                        </p>
                        <div className="space-y-2">
                            {user.empowerments && user.empowerments.length > 0 ? (
                                <div>
                                    <p className="text-sm text-gray-400 mb-2">
                                        Empowerments received:
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {user.empowerments.map((emp) => (
                                            <span
                                                key={emp}
                                                className="text-xs bg-amber-900/40 text-amber-300 border border-amber-700/50 px-2 py-0.5 rounded-full"
                                            >
                                                {emp}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">
                                    Empowerments: <span className="text-yellow-400">None yet</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
